import React, {useState} from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import {Link} from 'react-router-dom';
import {validateUsername, validateEmail, validateZipcode} from '../services/validationService';
import './SignupFormComponent.css';
import { apiClient } from '../api/ApiClient'; 
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../security/AuthContext';

const SignupFormComponent = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    gender: '',
    zipcode: '',
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    password: '',
    showPassword: false,
  });

 // Add this function to handle reCAPTCHA changes
  const handleRecaptchaChange = (value) => {
      setRecaptchaValue(value);
  };

  const navigate = useNavigate();
  const authContext = useAuth();

  const [errors, setErrors] = useState({});
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const genderOptions = [
    {value: '', label: '[Not selected]'},
    {value: 'M', label: 'Male'},
    {value: 'F', label: 'Female'}
  ];

  const days = Array.from({length: 31}, (_, i) => ({value: i + 1, label: i + 1}));
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ].map((month, i) => ({value: i + 1, label: month}));
  const currentYear = new Date().getFullYear();
  const years = Array.from({length: 100}, (_, i) => ({
    value: currentYear - i - 18,
    label: currentYear - i - 18
  }));

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 5) {
      newErrors.username = 'Username must be at least 5 characters';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    if (!formData.zipcode) {
      newErrors.zipcode = 'Zipcode is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (!/\d/.test(formData.password)) {
      newErrors.password = 'Password must contain at least 1 number';
    }

    if (!formData.birthDay || !formData.birthMonth || !formData.birthYear) {
      newErrors.birthDate = 'Complete birth date is required';
    }

    // Add reCAPTCHA validation
    if (!recaptchaValue) {
          newErrors.recaptcha = 'Please verify that you are not a robot';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const submitSignupStep1 = async (formData) => {
    try {
      // Format the data according to the backend's SignupStep1Request DTO
      const requestData = {
        username: formData.username,
        email: formData.email,
        gender: formData.gender === 'M' ? 'MALE' : 'FEMALE', // Convert to the format expected by backend
        zipcode: formData.zipcode,
        dobDay: formData.birthDay,
        dobMonth: formData.birthMonth,
        dobYear: formData.birthYear,
        password: formData.password,
        recaptchaResponse: formData.recaptchaToken
      };
      
      const response = await apiClient.post('/rest/signup1', requestData);

      return response.data;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };


const handleSubmit = async (e) => {
  e.preventDefault();
  console.log('Form submission attempted');
  setSubmitted(true);

  if (validateForm()) {
    console.log('Form validation passed');
    setIsSubmitting(true);
    
    try {
      // Prepare data for API
      const signupData = {
        username: formData.username,
        email: formData.email,
        gender: formData.gender === 'M' ? 'MALE' : 'FEMALE',
        zipcode: formData.zipcode,
        dobDay: formData.birthDay,
        dobMonth: formData.birthMonth,
        dobYear: formData.birthYear,
        password: formData.password,
        recaptchaResponse: recaptchaValue
      };
      
      // Call the API directly
      const response = await apiClient.post('/rest/signup1', signupData);
      
      if (response.data && response.data.token) {
        // Store the token in localStorage
        localStorage.setItem('token', response.data.token);
        
        // Update auth context
        authContext.setIsAuthenticated(true);
        authContext.setUsername(formData.username);
        authContext.setToken(response.data.token);
        
        // Set Authorization header for future requests
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        
        // Show success message
        setShowSubmitted(true);
        
        // Redirect after a short delay
        setTimeout(() => {
          navigate('/signup/step2');
        }, 1500);
      }
    } catch (error) {
      console.error('Signup error:', error);
      setErrors(prev => ({
        ...prev,
        general: error.response?.data?.message || 'An error occurred during signup'
      }));
    } finally {
      setIsSubmitting(false);
    }
  }
};



  const [validationStatus, setValidationStatus] = useState({
    username: {isValid: true, message: '', checking: false},
    email: {isValid: true, message: '', checking: false},
    zipcode: {isValid: true, message: '', checking: false, city: '', state: ''}
  });

  // Username validation
  const checkUsername = async (username) => {
    if (username.length < 5) return;

    setValidationStatus(prev => ({
      ...prev,
      username: {...prev.username, checking: true}
    }));

    try {
      const result = await validateUsername(username);
      setValidationStatus(prev => ({
        ...prev,
        username: {
          isValid: !result.exists,
          message: result.exists ? 'Username is already in use' : '',
          checking: false
        }
      }));
    } catch (error) {
      setValidationStatus(prev => ({
        ...prev,
        username: {
          isValid: false,
          message: 'Error checking username',
          checking: false
        }
      }));
    }
  };

  // Email validation
  const checkEmail = async (email) => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) return;

    setValidationStatus(prev => ({
      ...prev,
      email: {...prev.email, checking: true}
    }));

    try {
      const result = await validateEmail(email);
      setValidationStatus(prev => ({
        ...prev,
        email: {
          isValid: !result.exists,
          message: result.exists ? 'Email is already in use' : '',
          checking: false
        }
      }));
    } catch (error) {
      setValidationStatus(prev => ({
        ...prev,
        email: {
          isValid: false,
          message: 'Error checking email',
          checking: false
        }
      }));
    }
  };

  // Zipcode validation
  const checkZipcode = async (zipcode) => {
    if (zipcode.length !== 5) return;

    setValidationStatus(prev => ({
      ...prev,
      zipcode: {...prev.zipcode, checking: true}
    }));

    try {
      const result = await validateZipcode(zipcode);
      setValidationStatus(prev => ({
        ...prev,
        zipcode: {
          isValid: result.valid,
          message: result.valid ? '' : 'Invalid zipcode',
          checking: false,
          city: result.city || '',
          state: result.state || ''
        }
      }));
    } catch (error) {
      setValidationStatus(prev => ({
        ...prev,
        zipcode: {
          isValid: false,
          message: 'Error checking zipcode',
          checking: false,
          city: '',
          state: ''
        }
      }));
    }
  };

  // Handle input change - just update the form data without validation
  const handleInputChange = (e) => {
    const {name, value, type, checked} = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  //  Handle field blur - validate when user leaves a field
  const handleFieldBlur = (e) => {
    const {name, value} = e.target;
    
    if (!value) return;
    if (name === 'username') checkUsername(value);
    if (name === 'email') checkEmail(value);
    if (name === 'zipcode') checkZipcode(value);
  };

  return (
    <div className="signup-container">
      <div className="signup-form-wrapper">
        <div className="signup-heading">
          <h1>Thousands of New Singles Daily</h1>
        </div>
  
        <form onSubmit={handleSubmit}>
          {/* Username field */}
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              className={`${errors.username || !validationStatus.username.isValid ? 'error' : ''} 
                     ${validationStatus.username.checking ? 'checking' : ''}`}
            />
            {validationStatus.username.checking && <div className="checking-message">Checking...</div>}
            {validationStatus.username.message &&
              <div className="error-message">{validationStatus.username.message}</div>}
            {errors.username && <div className="error-message">{errors.username}</div>}
          </div>
  
          {/* Email field */}
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              className={`${errors.email || !validationStatus.email.isValid ? 'error' : ''} 
                     ${validationStatus.email.checking ? 'checking' : ''}`}
            />
            {validationStatus.email.checking && <div className="checking-message">Checking...</div>}
            {validationStatus.email.message &&
              <div className="error-message">{validationStatus.email.message}</div>}
            {errors.email && <div className="error-message">{errors.email}</div>}
          </div>
  
          {/* Gender and Zipcode row - FIXED */}
          <div className="form-row">
            <div className="form-group half">
              <label>My Gender</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className={errors.gender ? 'error' : ''}
              >
                <option value="">[Not selected]</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
              {errors.gender && <div className="error-message">{errors.gender}</div>}
            </div>

            <div className="form-group half">
              <label>Zip</label>
              <input
                type="text"
                name="zipcode"
                value={formData.zipcode}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                className={`${errors.zipcode || !validationStatus.zipcode.isValid ? 'error' : ''} 
                     ${validationStatus.zipcode.checking ? 'checking' : ''}`}
              />
              {validationStatus.zipcode.checking && <div className="checking-message">Checking...</div>}
              {validationStatus.zipcode.message &&
                <div className="error-message">{validationStatus.zipcode.message}</div>}
              {errors.zipcode && <div className="error-message">{errors.zipcode}</div>}
            </div>
          </div>

          {/* City and State display - MOVED OUTSIDE form-row to fix layout */}
          {validationStatus.zipcode.isValid && validationStatus.zipcode.city && validationStatus.zipcode.state && (
            <div className="location-info-outside">
              {validationStatus.zipcode.city}, {validationStatus.zipcode.state}
            </div>
          )}

          {/* Birth Date row */}
          <div className="form-group">
            <label>Birth Date</label>
            <div className="birth-date-row">
              <select name="birthDay" value={formData.birthDay} onChange={handleInputChange}>
                <option value="">[Day]</option>
                {days.map(day => (
                  <option key={day.value} value={day.value}>{day.label}</option>
                ))}
              </select>
              <select name="birthMonth" value={formData.birthMonth} onChange={handleInputChange}>
                <option value="">[Month]</option>
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
              <select name="birthYear" value={formData.birthYear} onChange={handleInputChange}>
                <option value="">[Year]</option>
                {years.map(year => (
                  <option key={year.value} value={year.value}>{year.label}</option>
                ))}
              </select>
            </div>
            {errors.birthDate && <div className="error-message">{errors.birthDate}</div>}
          </div>
  
          {/* Password field - FIXED */}
          <div className="form-group">
            <label>Password you want</label>
            <input
              type={formData.showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={errors.password ? 'error' : ''}
            />
            {errors.password && <div className="error-message">{errors.password}</div>}
            
            {/* Show password checkbox - MOVED OUTSIDE input */}
            <div className="show-password-outside">
              <label>
                <input
                  type="checkbox"
                  name="showPassword"
                  checked={formData.showPassword}
                  onChange={handleInputChange}
                />
                Show password
              </label>
            </div>
          </div>
  
          {/* ReCAPTCHA */}
          <div className="recaptcha-container">
          <ReCAPTCHA
          sitekey="6LfCSGgrAAAAAFarZ_RtZw0RiPWPHSMYc8i81KbA"
          onChange={handleRecaptchaChange}
          />
          {errors.recaptcha && <div className="error-message">{errors.recaptcha}</div>}
          </div>
  
          {/* Terms of Service */}
          <div className="terms-container">
            <label>
              <input type="checkbox" checked disabled />
              I Agree to the Terms of <Link to="/settings/terms-of-use">Service</Link>
            </label>
          </div>
  
          {/* Submit button */}
          <button 
            type="submit" 
            className={`create-account-btn ${isSubmitting ? 'submitting' : ''}`}
            disabled={isSubmitting}
             >
            {isSubmitting ? 'Creating Account...' : 'Create My Account'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SignupFormComponent;